import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Tabs, Tab, Button } from 'react-bootstrap';
import { useSearchParams, Link } from 'react-router-dom';
import { tripService, articleService, UPLOADS_BASE_URL } from '../services/api';
import { FaSearch, FaMapMarkerAlt, FaCalendarAlt, FaDollarSign, FaClock, FaEye } from 'react-icons/fa';
import SEO from '../components/SEO';
import EmptyState from '../components/EmptyState';
import { SkeletonGrid } from '../components/SkeletonLoader';
import './SearchResultsPage.css';

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [trips, setTrips] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (query) {
      performSearch();
    }
  }, [query]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const [tripsRes, articlesRes] = await Promise.all([
        tripService.getAllTrips({ search: query }),
        articleService.getArticles({ search: query, limit: 50 })
      ]);

      setTrips(tripsRes.data || []);
      setArticles(articlesRes.data.articles || []);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalResults = trips.length + articles.length;

  const renderTrips = () => {
    if (loading) {
      return <SkeletonGrid count={6} type="trip" />;
    }

    if (trips.length === 0) {
      return (
        <EmptyState
          type="search"
          title="No Trips Found"
          message={`We couldn't find any trips matching "${query}". Try different keywords or browse all trips.`}
          actionLabel="Browse All Trips"
          onAction={() => window.location.href = '/trips'}
        />
      );
    }

    return (
      <Row>
        {trips.map((trip) => (
          <Col key={trip._id} md={6} lg={4} className="mb-4">
            <Card className="trip-card h-100">
              <Link to={`/trips/${trip._id}`}>
                <Card.Img
                  variant="top"
                  src={`${UPLOADS_BASE_URL}/uploads/${trip.image}`}
                  alt={trip.name}
                  onError={(e) => { e.target.src = '/images/default-trip.jpg'; }}
                />
              </Link>
              <Card.Body>
                <Card.Title>
                  <Link to={`/trips/${trip._id}`} className="text-decoration-none text-dark">
                    {trip.name}
                  </Link>
                </Card.Title>
                <div className="trip-meta mb-2">
                  <small className="text-muted">
                    <FaMapMarkerAlt className="me-1" />
                    {trip.location}
                  </small>
                </div>
                <Card.Text className="trip-description">
                  {trip.description.substring(0, 100)}...
                </Card.Text>
                <div className="trip-footer d-flex justify-content-between align-items-center">
                  <div className="trip-price">
                    <FaDollarSign />
                    {trip.price}
                  </div>
                  <Badge bg="primary">{trip.difficulty}</Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderArticles = () => {
    if (loading) {
      return <SkeletonGrid count={6} type="article" />;
    }

    if (articles.length === 0) {
      return (
        <EmptyState
          type="search"
          title="No Articles Found"
          message={`We couldn't find any articles matching "${query}". Try different keywords or browse all articles.`}
          actionLabel="Browse All Articles"
          onAction={() => window.location.href = '/blog'}
        />
      );
    }

    return (
      <Row>
        {articles.map((article) => (
          <Col key={article._id} md={6} lg={4} className="mb-4">
            <Card className="article-card h-100">
              <Card.Img
                variant="top"
                src={`${UPLOADS_BASE_URL}/uploads/${article.featuredImage}`}
                alt={article.title}
                onError={(e) => { e.target.src = '/images/default-article.jpg'; }}
              />
              <Card.Body>
                <Badge bg="info" className="mb-2">
                  {article.category.replace('-', ' ')}
                </Badge>
                <Card.Title>
                  <Link to={`/blog/${article.slug}`} className="text-decoration-none text-dark stretched-link">
                    {article.title}
                  </Link>
                </Card.Title>
                <Card.Text className="article-excerpt">
                  {article.excerpt}
                </Card.Text>
                <div className="article-meta">
                  <small className="text-muted">
                    <FaClock className="me-1" />
                    {article.readTime} min read
                  </small>
                  <small className="text-muted ms-3">
                    <FaEye className="me-1" />
                    {article.views} views
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Container className="search-results-page py-5">
      <SEO
        title={`Search Results for "${query}" | DiveConnect`}
        description={`Search results for "${query}" - Find diving trips and articles on DiveConnect`}
      />

      <div className="search-header mb-4">
        <h1>
          <FaSearch className="me-3" />
          Search Results
        </h1>
        <p className="lead">
          {loading ? (
            'Searching...'
          ) : totalResults === 0 ? (
            <>No results found for <strong>"{query}"</strong></>
          ) : (
            <>
              Found <strong>{totalResults}</strong> result{totalResults !== 1 ? 's' : ''} for <strong>"{query}"</strong>
            </>
          )}
        </p>
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="search-tabs mb-4"
      >
        <Tab
          eventKey="all"
          title={`All (${totalResults})`}
        >
          {loading ? (
            <>
              <h3 className="mt-4">Trips</h3>
              <SkeletonGrid count={3} type="trip" />
              <h3 className="mt-5">Articles</h3>
              <SkeletonGrid count={3} type="article" />
            </>
          ) : (
            <>
              {trips.length > 0 && (
                <>
                  <h3 className="mt-4 mb-3">Trips ({trips.length})</h3>
                  {renderTrips()}
                </>
              )}
              
              {articles.length > 0 && (
                <>
                  <h3 className="mt-5 mb-3">Articles ({articles.length})</h3>
                  {renderArticles()}
                </>
              )}

              {totalResults === 0 && (
                <EmptyState
                  type="search"
                  title="No Results Found"
                  message={`We couldn't find anything matching "${query}". Try different keywords or browse our content.`}
                  actionLabel="Explore Trips"
                  onAction={() => window.location.href = '/trips'}
                />
              )}
            </>
          )}
        </Tab>

        <Tab
          eventKey="trips"
          title={`Trips (${trips.length})`}
        >
          {renderTrips()}
        </Tab>

        <Tab
          eventKey="articles"
          title={`Articles (${articles.length})`}
        >
          {renderArticles()}
        </Tab>
      </Tabs>
    </Container>
  );
};

export default SearchResultsPage;
